import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { pickNUniqueRandom } from "@/utils/helpers";
import { APP_VERSION } from "@/constants";
import { shouldInvalidateCache } from "@/utils/version.js";

import {
  getPropertiesConfig as getPropertiesConfigReq,
  getUserProperties as getUserPropertiesReq,
  syncUserProperties as syncUserPropertiesReq,
} from "@/api/repositories/user/properties";

import { useProfileStore } from "./profile";

export const usePropertiesStore = defineStore(
  "properties",
  () => {
    const propertiesConfig = ref([]);
    const userProperties = ref([]);
    const initialPromptFields = ref([]);
    const currentRandomPrompt = ref(null);
    const cacheAppVersion = ref(null);

    const profileStore = useProfileStore();

    const bioFields = computed(() =>
      propertiesConfig.value
        .filter((field) => field.category_name === "Bio")
        .sort((a, b) => a.display_order - b.display_order)
    );

    const lookingForBioOptions = computed(() => {
      const lookingForBio = bioFields.value.find((f) =>
        /looking for/gi.test(f.property_name)
      );
      if (!lookingForBio) return [];
      return lookingForBio.enum_values.map((v) => ({
        text: v.value,
        value: v.id,
      }));
    });

    const interestsFields = computed(() =>
      propertiesConfig.value
        .filter((field) => field.category_name === "Interests")
        .sort((a, b) => a.display_order - b.display_order)
    );

    const promptFields = computed(() =>
      propertiesConfig.value
        .filter((field) => field.category_name === "Sprinkle")
        .sort((a, b) => a.display_order - b.display_order)
    );

    const textPromptFields = computed(() =>
      promptFields.value.filter((p) => p.validation_type === "text")
    );

    const imagePromptFields = computed(() =>
      promptFields.value.filter((p) => p.validation_type === "link")
    );

    const spotifyPromptFields = computed(() =>
      promptFields.value.filter((p) => p.validation_type === "spotify")
    );

    const selectedInterests = computed(() =>
      profileStore.profile?.properties
        .filter((prop) => prop.category_name === "Interests")
        .map((prop) => prop.value)
        .filter(Boolean)
    );

    const answeredPrompts = computed(() =>
      profileStore.profile?.properties
        .filter((prop) => prop.category_name === "Sprinkle")
        .filter(Boolean)
    );

    const answeredBio = computed(() =>
      profileStore.profile?.properties
        .filter((prop) => prop.category_name === "Bio")
        .filter(Boolean)
    );

    const getPropertiesConfig = async () => {
      const { config } = await getPropertiesConfigReq();
      propertiesConfig.value = config;
      setInitialPromptFields();
    };

    const getUserProperties = async () => {
      const { properties } = await getUserPropertiesReq();
      userProperties.value = properties;
      profileStore.profile.properties = properties;
    };

    const syncUserProperties = async (data) => {
      const { user } = await syncUserPropertiesReq({
        properties: data,
      });
      profileStore.profile = user;
      userProperties.value = user.properties;
    };

    const getRandomPromptFields = () => {
      const randomTextPrompts = pickNUniqueRandom(textPromptFields.value, 2);
      const randomImagePrompts = pickNUniqueRandom(imagePromptFields.value, 2);
      const randomSpotifyPrompts = pickNUniqueRandom(
        spotifyPromptFields.value,
        2
      );
      const prompts = [
        ...randomTextPrompts,
        ...randomImagePrompts,
        ...randomSpotifyPrompts,
      ];
      return prompts.filter(Boolean);
    };

    const isInitialPromptCacheValid = () => {
      if (!initialPromptFields.value?.length > 0) return false;

      if (shouldInvalidateCache(APP_VERSION, cacheAppVersion.value)) {
        return false;
      }

      return true;
    };

    const setInitialPromptFields = () => {
      if (isInitialPromptCacheValid()) {
        return;
      }

      initialPromptFields.value = getRandomPromptFields();
      cacheAppVersion.value = APP_VERSION;
      currentRandomPrompt.value = null;
    };

    // Get the current random prompt, or generate a new one if none exists
    const getCurrentRandomPrompt = () => {
      return currentRandomPrompt.value;
    };

    // Set the current random prompt
    const setCurrentRandomPrompt = (prompt) => {
      currentRandomPrompt.value = prompt;
    };

    // Clear the current random prompt
    const clearCurrentRandomPrompt = () => {
      currentRandomPrompt.value = null;
    };

    return {
      propertiesConfig,
      cacheAppVersion,
      bioFields,
      lookingForBioOptions,
      interestsFields,
      promptFields,
      textPromptFields,
      imagePromptFields,
      spotifyPromptFields,
      userProperties,
      selectedInterests,
      answeredPrompts,
      answeredBio,
      currentRandomPrompt,
      getPropertiesConfig,
      getUserProperties,
      syncUserProperties,
      initialPromptFields,
      getCurrentRandomPrompt,
      setCurrentRandomPrompt,
      clearCurrentRandomPrompt,
    };
  },
  {
    persist: {
      paths: ["initialPromptFields", "cacheAppVersion"],
    },
  }
);
